import React, { createContext, useContext, useState, useEffect } from 'react';

const ThemeContext = createContext();

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

export const ThemeProvider = ({ children }) => {
  const [isDarkMode, setIsDarkMode] = useState(() => {
    // Get from localStorage or default to true (dark mode)
    const saved = localStorage.getItem('theme');
    if (saved) {
      return saved === 'dark';
    }
    // Default to dark mode
    return true;
  });

  const toggleTheme = () => {
    const newTheme = !isDarkMode;
    setIsDarkMode(newTheme);
    localStorage.setItem('theme', newTheme ? 'dark' : 'light');
    // Update HTML class
    if (newTheme) {
      document.documentElement.classList.add('dark');
      document.documentElement.classList.remove('light');
    } else {
      document.documentElement.classList.add('light');
      document.documentElement.classList.remove('dark');
    }
  };

  useEffect(() => {
    // Set initial theme class
    if (isDarkMode) {
      document.documentElement.classList.add('dark');
      document.documentElement.classList.remove('light');
    } else {
      document.documentElement.classList.add('light');
      document.documentElement.classList.remove('dark');
    }
  }, [isDarkMode]);

  // Sync theme-color meta for mobile browser chrome (address bar, etc.)
  useEffect(() => {
    let meta = document.querySelector('meta[name="theme-color"]');
    if (!meta) {
      meta = document.createElement('meta');
      meta.name = 'theme-color';
      document.head.appendChild(meta);
    }
    meta.content = isDarkMode ? '#121915' : '#F2F2F7';
  }, [isDarkMode]);

  // Theme classes helper — BetterApp NAV_THEME via CSS variables (canvas/surface/ink/forest)
  const themeClasses = {
    bgPrimary: 'bg-canvas',
    bgCard: 'bg-surface dark:bg-surface-dark',
    textPrimary: 'text-ink dark:text-ink-dark',
    textSecondary: 'text-ink-muted dark:text-ink-muted-dark',
    textMuted: 'text-ink-muted dark:text-ink-muted-dark',
    borderPrimary: 'border-line dark:border-line-dark',
    shadowCard: isDarkMode ? 'shadow-lg shadow-black/20' : 'shadow-sm',
    shadowHover: isDarkMode ? 'hover:shadow-xl hover:shadow-black/30' : 'hover:shadow-md',
    btnPrimary: 'bg-forest hover:bg-forest-hover text-white',
  };

  return (
    <ThemeContext.Provider value={{ isDarkMode, toggleTheme, themeClasses }}>
      {children}
    </ThemeContext.Provider>
  );
};

